import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { Toaster } from '@/components/ui/sonner';
import SessionManager from '@/components/SessionManager';
import LoginPage from '@/pages/LoginPage';
import ConformidadPage from '@/pages/ConformidadPage';
import ConsultaPage from '@/pages/ConsultaPage';
import SiAlgoSeRompe from '@/components/SiAlgoSeRompe';
import AppLayout from '@/pages/AppLayout';
import DashboardPage from '@/pages/DashboardPage';
import MyWorkPage from '@/pages/MyWorkPage';
import ChatPage from '@/pages/ChatPage';
import TicketsPage from '@/pages/TicketsPage';
import TaskPage from '@/pages/TaskPage';
import AgentsPage from '@/pages/AgentsPage';
import ConexionesPage from '@/pages/ConexionesPage';
import MonitorPage from '@/pages/MonitorPage';
import KnowledgePage from '@/pages/KnowledgePage';
import ChannelsPage from '@/pages/ChannelsPage';
import CapsuleConfigPage from '@/pages/CapsuleConfigPage';
import CapsulesPage from '@/pages/CapsulesPage';
import LogsPage from '@/pages/LogsPage';
import TeamPage from '@/pages/TeamPage';
import PersonasPage from '@/pages/PersonasPage';
import MisSolicitudesPage from '@/pages/MisSolicitudesPage';
import ExecutivePage from '@/pages/ExecutivePage';
import ExecutiveGanttPage from '@/pages/ExecutiveGanttPage';
import CategoriasPage from '@/pages/CategoriasPage';
import DireccionPage from '@/pages/DireccionPage';
import ManualPage from '@/pages/ManualPage';

const ADMIN_ROLES = new Set(['admin', 'supervisor']);

function defaultHomeForRole(role) {
  // Quien solo pidio algo no tiene "trabajo" que mirar: `/my-work` le saldria vacio y
  // concluiria que la app no anda.
  if (role === 'solicitante') return '/mis-solicitudes';
  return role === 'operator' || role === 'viewer' ? '/my-work' : '/dashboard';
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <AuthLoadingScreen />;
  }
  return user ? children : <Navigate to="/login" replace />;
}

/**
 * Quién entra a una pantalla.
 *
 * **La lista dejó de estar acá.** Hasta el 2026-08-24 cada ruta llevaba su
 * `roles={['admin','supervisor']}` escrito a mano, y darle el tablero ejecutivo a un rol de
 * consulta exigía tocar el repo y desplegar. Ahora lo declara la cápsula y viaja con la sesión:
 * `user.pantallas`.
 *
 * **`roles` sigue existiendo como red.** Si la sesión es vieja y no trae `pantallas` —una pestaña
 * abierta desde antes del deploy—, manda la lista de siempre. Sin esa red, el día del deploy
 * cualquiera con una sesión cacheada se quedaría afuera de todo.
 *
 * **Y esto no protege el dato.** Cada endpoint sigue exigiendo lo suyo: esconder una pantalla es
 * decidir qué se le ofrece a alguien, que es otra pregunta. Ofrecer lo que el backend rechaza es
 * peor que no ofrecerlo — la persona no aprende la regla, aprende que la aplicación falla.
 */
function RoleRoute({ children, roles, pantalla }) {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (pantalla && Array.isArray(user.pantallas)) {
    if (!user.pantallas.includes(pantalla)) {
      return <Navigate to={defaultHomeForRole(user.role)} replace />;
    }
    return children;
  }
  // **Sin `pantallas` en el usuario, una ruta que solo declara `pantalla` deja pasar.** Es el lado
  // seguro del error y es deliberado: el token viejo de una sesión abierta no trae el campo, y
  // bloquear ahí dejaría a una persona sin «Mi trabajo» hasta que vuelva a entrar. El dato sigue
  // protegido por el permiso de cada endpoint; esto es navegación.
  if (!roles) return children;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={defaultHomeForRole(user.role)} replace />;
  }
  return children;
}

function AuthLoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <span className="font-heading text-2xl font-bold text-[#09090B] tracking-tighter">OMNISYS</span>
        <p className="text-xs text-zinc-400 mt-2 font-mono">Cargando...</p>
      </div>
    </div>
  );
}

function PublicRoute({ children }) {
  const { user, loading, consumeReturnUrl } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (user) {
    const returnUrl = consumeReturnUrl();
    const fallback = defaultHomeForRole(user.role);
    return <Navigate to={returnUrl || fallback} replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <SessionManager />
        {/* **Una pantalla en blanco es la peor forma de fallar de una interfaz.** Va acá adentro
            del router para que el menú y la navegación sobrevivan: se rompe la sección, no la
            aplicación. Ver components/SiAlgoSeRompe.jsx. */}
        <Routes>
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          {/* **Sin sesión, y a propósito.** El que pidió el caso puede no tener cuenta: escribió
              un WhatsApp o un correo y nunca entró acá. Ver routers/conformidad.py. */}
          <Route path="/conformidad/:token" element={<ConformidadPage />} />
          <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route index element={<HomeRedirect />} />
            {/* **Las cinco que la matriz declara y el router no hacia cumplir.**
                `pantallas_por_rol` las presenta como configurables —y la UI de Config Cápsula deja
                quitárselas a un rol— pero estas rutas no tenían `RoleRoute`: el ítem desaparecía del
                menú y **la URL seguía funcionando**. No abría datos nuevos —cada endpoint sigue
                exigiendo lo suyo— pero sí una creencia falsa en la pantalla donde alguien diseña
                accesos, que es el peor lugar para tenerla.
                Van con `pantalla` y **sin** `roles`: el default de la matriz copia lo que había
                —las cinco para todos los roles— así que nadie pierde nada hoy, y a partir de ahora
                quitárselas a un rol hace lo que la pantalla promete. */}
            <Route path="mis-solicitudes" element={<RoleRoute pantalla="mis_solicitudes"><MisSolicitudesPage /></RoleRoute>} />
            <Route path="my-work" element={<RoleRoute pantalla="mi_trabajo"><MyWorkPage /></RoleRoute>} />
            <Route path="dashboard" element={<RoleRoute roles={['admin', 'supervisor', 'executive']} pantalla="tablero"><DashboardPage /></RoleRoute>} />
            <Route path="manual" element={<RoleRoute roles={['admin', 'supervisor']} pantalla="manual"><ManualPage /></RoleRoute>} />
            <Route path="direccion" element={<RoleRoute roles={['admin', 'supervisor', 'executive']} pantalla="direccion"><DireccionPage /></RoleRoute>} />
            <Route path="executive" element={<RoleRoute roles={['admin', 'supervisor', 'executive']} pantalla="ejecutivo"><ExecutivePage /></RoleRoute>} />
            <Route path="executive/gantt" element={<RoleRoute roles={['admin', 'supervisor', 'executive']} pantalla="ejecutivo"><ExecutiveGanttPage /></RoleRoute>} />
            <Route path="executive/categorias" element={<RoleRoute roles={['admin', 'supervisor', 'executive']} pantalla="ejecutivo"><CategoriasPage /></RoleRoute>} />
            <Route path="chat" element={<RoleRoute roles={['admin', 'supervisor']} pantalla="chat"><ChatPage /></RoleRoute>} />
            <Route path="tickets" element={<RoleRoute pantalla="casos"><TicketsPage /></RoleRoute>} />
            {/* Preguntar sin abrir un caso. Lo pidieron los que trabajan, y el Playground estaba
                restringido justamente a los que no preguntan — por eso el default de la matriz la
                da a todos menos al solicitante: *para él, preguntar **es** abrir un caso*. */}
            <Route path="consulta" element={<RoleRoute pantalla="consulta"><ConsultaPage /></RoleRoute>} />
            {/* La ficha de una tarea cuelga de «Casos»: es la misma pantalla un nivel abajo, y
                tener una sin la otra deja un link roto adentro del producto. */}
            <Route path="tickets/:ticketId/tasks/:taskId" element={<RoleRoute pantalla="casos"><TaskPage /></RoleRoute>} />
            <Route path="agents" element={<RoleRoute roles={['admin', 'supervisor']} pantalla="agentes"><AgentsPage /></RoleRoute>} />
            <Route path="monitor" element={<RoleRoute pantalla="monitor"><MonitorPage /></RoleRoute>} />
            <Route path="inbox" element={<Navigate to="/monitor" replace />} />
            <Route path="knowledge" element={<RoleRoute roles={['admin', 'supervisor']} pantalla="conocimiento"><KnowledgePage /></RoleRoute>} />
            <Route path="channels" element={<RoleRoute roles={['admin', 'supervisor']} pantalla="canales"><ChannelsPage /></RoleRoute>} />
            <Route path="conexiones" element={<RoleRoute roles={['admin', 'supervisor']} pantalla="conexiones"><ConexionesPage /></RoleRoute>} />
            <Route path="capsules" element={<RoleRoute roles={['admin', 'supervisor']} pantalla="capsulas"><CapsulesPage /></RoleRoute>} />
            <Route path="capsule-config" element={<RoleRoute roles={['admin', 'supervisor']} pantalla="config_capsula"><CapsuleConfigPage /></RoleRoute>} />
            <Route path="team" element={<RoleRoute roles={['admin', 'supervisor']} pantalla="equipo"><TeamPage /></RoleRoute>} />
            <Route path="personas" element={<RoleRoute roles={['admin', 'supervisor', 'agent', 'operator']} pantalla="personas"><PersonasPage /></RoleRoute>} />
            {/* Contacto, usuario y miembro del equipo pasaron a ser una sola noción de
                persona el 2026-08-18. El link viejo sigue vivo: hay marcadores guardados. */}
            <Route path="contacts" element={<Navigate to="/personas" replace />} />
            <Route path="logs" element={<RoleRoute roles={['admin', 'supervisor']} pantalla="registros"><LogsPage /></RoleRoute>} />
          </Route>
          <Route path="*" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </AuthProvider>
  );
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  return <Navigate to={defaultHomeForRole(user?.role)} replace />;
}

export default App;
